import type { CSSProperties } from "react";
import { PriceChart, type PriceChartRefLine } from "./price-chart/price-chart";
import { usePerpChart } from "../hooks/use-perp-chart";
import type { PerpLeg } from "../lib/composePayoff";
import type { SelectedPerp } from "../types";

/** Violet-400 — left side perp chart color. */
export const PERP_COLOR = "#a78bfa";

const emptyStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--fg-dim)",
  border: "1px dashed var(--line-3)",
  margin: 12,
  borderRadius: 2,
};

export function PerpChartView({
  selected,
  leg,
}: {
  selected: SelectedPerp | null;
  /** When a simulated position exists, overlay entry + liquidation lines. */
  leg?: PerpLeg | null;
}) {
  const { data, currentMid, error } = usePerpChart(selected?.coin ?? null);

  if (!selected) {
    return <div style={emptyStyle}>SELECT A PERP</div>;
  }
  if (error) {
    return (
      <div style={{ ...emptyStyle, color: "var(--sev-crit)" }}>
        FAILED TO LOAD — {error.message}
      </div>
    );
  }

  const refLines: PriceChartRefLine[] | undefined = leg
    ? [
        {
          value: leg.entry,
          color: PERP_COLOR,
          label: `ENTRY ${leg.side.toUpperCase()}`,
          dash: "4 4",
          labelAlign: "left",
        },
        {
          value: leg.liquidation,
          color: "var(--sev-crit)",
          label: "LIQ",
          dash: "2 4",
          labelAlign: "left",
        },
      ]
    : undefined;

  return (
    <div style={{ flex: 1, minHeight: 0, padding: 8 }}>
      <PriceChart
        data={data}
        lineColor={PERP_COLOR}
        assetName={selected.coin}
        currentPrice={currentMid}
        curveType="monotoneX"
        numXTicks={5}
        numYTicks={5}
        margin={TOP_CHART_MARGIN}
        showLiveDot
        refLines={refLines}
      />
    </div>
  );
}

/**
 * Shared margin between the perp and prediction top charts so their x-axes
 * line up horizontally. `right` is tight enough that cents labels on the
 * prediction side fit without extra whitespace.
 */
export const TOP_CHART_MARGIN = {
  top: 14,
  right: 48,
  bottom: 28,
  left: 14,
} as const;
